import { useState, useEffect, useRef } from 'react';

// ─── GOOGLE SHEETS WEBHOOK CONFIGURATION ──────────────────────────────────
// Set your Google Apps Script Web App URL here to save form data directly.
const GOOGLE_SHEETS_WEBAPP_URL = 'https://script.google.com/macros/s/AKfycbzfOITDsHJ8butUVQwjEfcG53vdn7c-QrHGc2mS6avAsECY1ZU9nhm2HJXFxWRcB6sx/exec'; 
// ─── DATA ─────────────────────────────────────────────────────────────────

const SERVICES = [
  { icon: '📣', title: 'Digital Marketing', desc: 'Full-funnel campaigns across every digital channel — built to generate leads, not just impressions.', tag: 'Marketing' },
  { icon: '🔍', title: 'SEO & Content', desc: 'Rank higher, stay there. We build search strategies grounded in technical SEO, authority, and content depth.', tag: 'SEO' },
  { icon: '💻', title: 'Web Development', desc: 'Fast, scalable, and conversion-focused websites — from landing pages to full enterprise web apps.', tag: 'Development' },
  { icon: '📱', title: 'Mobile App Development', desc: 'Native iOS & Android apps and cross-platform solutions that users actually want to open every day.', tag: 'Mobile' },
  { icon: '🎯', title: 'Google Ads (PPC)', desc: 'Data-driven paid search campaigns with tight targeting, smart bidding, and ROI you can measure in real time.', tag: 'Paid Ads' },
  { icon: '📘', title: 'Facebook & Instagram Ads', desc: 'Creative-led social ad campaigns on Meta that drive traffic, retarget warm audiences, and scale profitably.', tag: 'Social Ads' },
  { icon: '💬', title: 'Social Media Management', desc: 'Consistent, on-brand social presence — content calendars, community management, and monthly reporting.', tag: 'SMM' },
  { icon: '🤖', title: 'AI Chatbots', desc: 'Custom chatbots for sales, support, and lead qualification — integrated with your CRM and available 24/7.', tag: 'Automation' },
  { icon: '⚙️', title: 'Marketing Automation', desc: 'Email sequences, CRM workflows, and funnel automations that work while you sleep.', tag: 'Automation' },
  { icon: '🎨', title: 'Brand Identity & Design', desc: 'Logos, brand systems, and visual identities that make you look like the category leader you want to be.', tag: 'Design' },
  { icon: '📊', title: 'Analytics & Reporting', desc: 'Custom dashboards, attribution models, and monthly performance reports — no vanity metrics, only decisions.', tag: 'Analytics' },
  { icon: '🛒', title: 'E-Commerce Solutions', desc: 'Shopify, WooCommerce, and custom storefronts — with CRO baked in from day one.', tag: 'E-Commerce' },
];

const SERVICE_DETAILS = {
  'Digital Marketing': {
    tagline: 'Drive high-intent traffic and scale your customer acquisition.',
    overview: 'We design full-funnel digital marketing campaigns that capture ready-to-buy leads, boost brand loyalty, and scale customer lifetime value. No vanity metrics — just revenue-driven results.',
    features: ['Multi-channel campaign strategy', 'High-converting landing pages', 'Continuous A/B testing & audience refining', 'End-to-end multi-touch attribution setups'],
    faqs: [
      { q: 'How long does it take to see tangible results?', a: 'While paid ads generate traffic and leads within 48 hours, fully optimizing campaigns for peak ROI typically takes between 30 and 60 days.' },
      { q: 'Which ad channels are best for my business?', a: 'We perform a thorough competitor and audience audit to identify where your customers spend their time, usually focusing on Google, Meta, or LinkedIn.' }
    ]
  },
  'SEO & Content': {
    tagline: 'Rank first, build authority, and drive perpetual organic traffic.',
    overview: 'Get found by customers searching for your products or solutions. Our technical SEO audits, semantic keyword mapping, and authority building ensure your search visibility grows month-over-month.',
    features: ['Deep technical and architectural SEO audits', 'Intent-based keyword mapping & content calendars', 'High-quality editorial link acquisition', 'Local SEO & Schema markup implementation'],
    faqs: [
      { q: 'Is SEO a one-time process?', a: 'No. SEO is an ongoing asset. Search algorithms change constantly, and competitors constantly optimize their positions.' },
      { q: 'When can we expect page-one rankings?', a: 'Most clients see substantial search visibility growth in 2-3 months, with competitive keywords ranking on page one within 4-6 months.' }
    ]
  },
  'Web Development': {
    tagline: 'Blazing fast, secure, and custom-coded websites built to convert.',
    overview: 'Your website is your best salesperson. We develop clean, responsive, and secure custom sites in React and Next.js that look exceptional, load instantly, and turn visitors into buyers.',
    features: ['Custom React, Next.js, and headless CMS builds', 'Core Web Vitals optimized for perfect speed scores', 'Responsive design styled for all mobile screens', 'Integration with CRMs, scheduling, and payment portals'],
    faqs: [
      { q: 'What tech stack do you recommend for sites?', a: 'We build on Next.js, React, and Tailwind CSS or vanilla CSS. This stack guarantees maximum security, scalability, and unmatched loading speed.' },
      { q: 'Will we be able to edit the website content?', a: 'Yes. We build user-friendly dashboard panels or integrate headless CMS platforms (Sanity, Contentful) for easy content management.' }
    ]
  },
  'Mobile App Development': {
    tagline: 'Premium iOS & Android apps with seamless user experiences.',
    overview: 'Develop native or cross-platform mobile apps with frictionless user flows, beautiful micro-animations, offline capabilities, and scalable backend infrastructures.',
    features: ['Cross-platform development (Flutter, React Native)', 'Frictionless UI/UX design with responsive micro-animations', 'Real-time databases and offline support sync', 'App Store and Play Store compliance and publishing'],
    faqs: [
      { q: 'Should we build native or cross-platform apps?', a: 'We recommend cross-platform frameworks like Flutter, which slash development costs and launch times by 50% while preserving native look and speed.' },
      { q: 'Do you help publish the app to the stores?', a: 'Yes, we take care of the entire store deployment process, metadata optimizations, assets, and guidelines verification.' }
    ]
  },
  'Google Ads (PPC)': {
    tagline: 'Maximize search visibility and capture high-intent buyers.',
    overview: 'Bid on the terms your prospective clients are actively typing. We handle everything from negative keywords audits to high-converting ad copies and conversion-optimized landing pages.',
    features: ['High-intent search & shopping campaign builds', 'Rigorous negative keyword management to avoid waste', 'Persuasive copy and dynamic search ad variants', 'Automated bidding configuration & ROI monitoring'],
    faqs: [
      { q: 'What ad spend budget do we need to start?', a: 'We suggest starting with a testing budget that permits at least 10-20 clicks per day, typically starting around $500–$1,000/month.' },
      { q: 'How do you monitor and optimize our spend?', a: 'We review accounts daily, adjusting bids, negative matching, and testing new landing page layouts to lower CPA continuously.' }
    ]
  },
  'Facebook & Instagram Ads': {
    tagline: 'Stop the scroll, build desire, and scale social commerce.',
    overview: 'Reach your audience before they search for you. Our Meta ad campaigns utilize creative-led marketing, detailed audience lookup mapping, and smart retargeting grids to generate purchases.',
    features: ['Scroll-stopping visual designs and video creatives', 'Lookalike audience modeling and demographic matching', 'High-value retargeting and abandonment sequences', 'Meta Pixel and Conversions API implementation'],
    faqs: [
      { q: 'Do you design the creatives and write the copy?', a: 'Yes, we craft all ad variations—images, video hook clips, carousel frames, and conversion-copy.' },
      { q: 'How does Apple’s iOS privacy impact tracking?', a: 'We deploy Meta Conversions API (server-side tracking) to bypass cookie restrictions and guarantee accurate marketing data.' }
    ]
  },
  'Social Media Management': {
    tagline: 'Engage your community and build an organic brand voice.',
    overview: 'Ensure your brand remains active, helpful, and highly authoritative. We manage visual assets, caption scripting, calendar setups, and community engagement to keep followers connected.',
    features: ['Platform-specific social media strategy planning', 'Premium graphic cards, short clips, and carousel designs', 'Active community interaction, inbox management, and replies', 'In-depth monthly audience growth & engagement reviews'],
    faqs: [
      { q: 'Which platforms should my brand focus on?', a: 'For B2B brands, LinkedIn is key. For consumer-facing brands, Instagram, TikTok, and Facebook drive the best returns.' },
      { q: 'Do we get to approve the content first?', a: 'Yes. We share a complete visual content calendar for your review and approval before any post goes live.' }
    ]
  },
  'AI Chatbots': {
    tagline: 'Automate qualified leads, support queries, and bookings 24/7.',
    overview: 'Deploy smart conversational systems integrated with OpenAI and your CRM. Handle thousands of leads, answer frequently asked questions, and secure calendar meetings automatically.',
    features: ['Custom GPT agents trained on your specific documentation', 'Frictionless CRM sync, lead routing, and calendar scheduling', 'Multi-channel deploy: Web, WhatsApp Business, Messenger', 'Smooth human takeover handoffs for VIP customers'],
    faqs: [
      { q: 'Can the AI bot answer complex customer questions?', a: 'Yes, we feed the LLM your company knowledge base, catalogs, and guidelines so it answers accurately in natural language.' },
      { q: 'Is it possible to deploy the chatbot on WhatsApp?', a: 'Absolutely. We integrate official WhatsApp Business APIs, allowing your bot to interact directly in user chats.' }
    ]
  },
  'Marketing Automation': {
    tagline: 'Nurture leads and streamline operations while you sleep.',
    overview: 'Free up your team’s time by automating repetitive workflows. We connect your lead channels, CRM systems, and email tools to construct automated nurturing funnels that drive conversions.',
    features: ['Email and SMS workflows for automated lead nurture', 'Frictionless CRM pipeline tracking and lead scoring', 'Automation of customer onboarding and loyalty programs', 'Data integrations via Zapier, Make, and webhooks'],
    faqs: [
      { q: 'Which automation tools do you work with?', a: 'We work with industry standards like ActiveCampaign, HubSpot, Klaviyo, Zapier, and leading CRMs.' },
      { q: 'Will this replace our existing sales team CRM?', a: 'No, it enhances it. We connect automation triggers to your CRM to save your sales representatives hours of manual data input.' }
    ]
  },
  'Brand Identity & Design': {
    tagline: 'Stand out with a cohesive, world-class visual identity.',
    overview: 'Visual authority determines pricing power. We design logos, color books, brand assets, and layout systems that elevate your brand and ensure it looks like a market leader.',
    features: ['Sleek logo systems, typography, and vector assets', 'Complete brand style books and corporate manuals', 'Templates for slide decks, socials, and print collateral', 'Comprehensive brand positioning audits'],
    faqs: [
      { q: 'What assets do we receive upon completion?', a: 'You receive high-quality source vector files (AI, SVG, EPS), brand books, font files, and styles guidelines.' },
      { q: 'What is your logo creation workflow?', a: 'We explore 3 unique logo directions, iterate on your favorite pick, and refine details through up to 3 rounds of review.' }
    ]
  },
  'Analytics & Reporting': {
    tagline: 'Unify your data pools and make confident business choices.',
    overview: 'Stop guessing which ad budget performs best. We set up comprehensive attribution tracking and construct dashboard boards that display your real acquisition costs and revenue metrics.',
    features: ['Google Analytics 4 (GA4) configuration & tracking checks', 'Custom real-time reporting via Looker Studio dashboards', 'Reliable e-commerce and multi-channel attribution setups', 'Bi-weekly video walkthrough briefs of your metrics'],
    faqs: [
      { q: 'Are we able to check our dashboard reports 24/7?', a: 'Yes, our reporting dashboards are cloud-hosted, fully interactive, and update automatically every single hour.' },
      { q: 'Can you track offline phone calls and conversions?', a: 'Yes, we set up call attribution tracking to measure which ads generate telephone calls and leads.' }
    ]
  },
  'E-Commerce Solutions': {
    tagline: 'Build high-converting online storefronts that scale.',
    overview: 'Optimize every stage of your buying funnel. From Shopify configurations to WooCommerce customization, we develop online stores optimized for loading speeds and sales conversions.',
    features: ['Shopify & WooCommerce design, setup, and custom coding', 'Conversion rate optimization (CRO) checkouts', 'Seamless database migrations of products and customers', 'Integrations with shipping APIs and secure gateways'],
    faqs: [
      { q: 'Can you transfer our existing store database?', a: 'Yes. We migrate products, client databases, historical orders, and secure URL redirects to keep your SEO safe.' },
      { q: 'Do you help optimize store loading speeds?', a: 'Yes. We audit app scripts, optimize item assets, and customize code loops to hit perfect speed scores.' }
    ]
  }
};

// 20 Client Reviews (American profiles, natural text detail)
const REVIEWS = [
  { name: 'John Davis', role: 'CEO, Davis Trading Co', service: 'Digital Marketing', rating: 5, text: 'Corvaflex completely transformed our digital presence. Within 3 months our inbound leads doubled and cost per acquisition dropped by 40%. Truly professional team.', color: '#2563EB', init: 'JD' },
  { name: 'Sarah Jenkins', role: 'Founder, StyleHive', service: 'Facebook & Instagram Ads', rating: 5, text: 'Our Facebook ROAS went from 1.8x to 5.2x in just 6 weeks. The team really understands creative strategy and audience segmentation better than anyone I have worked with.', color: '#7C3AED', init: 'SJ' },
  { name: 'Michael Miller', role: 'Director, TechNova Solutions', service: 'Web Development', rating: 5, text: 'They delivered our entire SaaS product landing page in 10 days — responsive, fast, and exactly what we envisioned. Will work with them again without hesitation.', color: '#0891B2', init: 'MM' },
  { name: 'Emily Smith', role: 'Marketing Head, BrightEdu Group', service: 'SEO & Content', rating: 5, text: 'Our organic traffic grew 3x in 4 months after Corvaflex took over our SEO. We are now ranking page one for 17 keywords we could not crack before.', color: '#059669', init: 'ES' },
  { name: 'David Wilson', role: 'Owner, AutoZone USA', service: 'Google Ads (PPC)', rating: 5, text: 'I was spending money on Google Ads with no real results. Corvaflex restructured everything and now we are getting qualified leads at half the previous cost.', color: '#DC2626', init: 'DW' },
  { name: 'Chloe Thompson', role: 'Co-Founder, GreenLeaf Inc', service: 'AI Chatbots', rating: 5, text: 'The AI chatbot they built for us handles 70% of customer queries automatically. Our support team is finally focused on complex issues only. Game-changing.', color: '#D97706', init: 'CT' },
  { name: 'Robert Taylor', role: 'MD, Apex Logistics', service: 'Social Media Management', rating: 5, text: 'Our LinkedIn following grew from 400 to 8,200 in 5 months. The content strategy they created positions us as thought leaders in our niche — priceless.', color: '#2563EB', init: 'RT' },
  { name: 'Jessica Williams', role: 'CMO, FreshMart Group', service: 'E-Commerce Solutions', rating: 5, text: 'They built our Shopify store from scratch and embedded CRO tactics throughout. Our conversion rate went from 0.8% to 3.1% in the first month live.', color: '#7C3AED', init: 'JW' },
  { name: 'Brian Anderson', role: 'CEO, CodeCraft Studios', service: 'Mobile App Development', rating: 5, text: 'Corvaflex delivered our mobile app on time and on budget. The UI is clean, the code quality is excellent, and they were communicative throughout every sprint.', color: '#0891B2', init: 'BA' },
  { name: 'Ashley Martinez', role: 'Founder, PetPlus Group', service: 'Digital Marketing', rating: 5, text: 'From zero online presence to generating 150+ monthly orders through digital channels — Corvaflex made it happen in less than a year. Could not be happier.', color: '#059669', init: 'AM' },
  { name: 'Marcus Brody', role: 'VP of Growth, Apex Lending Co.', service: 'Google Ads (PPC)', rating: 5, text: 'Our search ad campaigns were draining thousands of dollars in wasted bids. Corvaflex audited our keywords and rebuilt our search ads structure. Our CPA dropped from $92 to $34 in 30 days, and the lead quality is outstanding.', color: '#DC2626', init: 'MB' },
  { name: 'Amanda Lewis', role: 'Founder, FreshBites Delivery', service: 'SEO & Content', rating: 5, text: 'We migrated our site to a new domain and were terrified of losing our organic traffic. The team at Corvaflex mapped out the redirect structure and technical SEO seamlessly. Our rankings didn\'t drop at all — traffic grew 20% post-migration.', color: '#059669', init: 'AL' },
  { name: 'Robert Sterling', role: 'E-Commerce Manager, FitGear Co.', service: 'Marketing Automation', rating: 5, text: 'The email automation sequences they built in Klaviyo have been a goldmine. Our abandoned checkout recovery went from 3% to 14%, directly adding $14k in monthly recurring revenue without spending a dollar more on ads.', color: '#7C3AED', init: 'RS' },
  { name: 'Jennifer Vance', role: 'Tech Lead, HealthLogix App', service: 'Mobile App Development', rating: 5, text: 'We hired Corvaflex to build our mobile client portal in Flutter. Their developers were highly collaborative, delivered two weeks ahead of schedule, and optimized the code for fast load times. Highly recommend.', color: '#0891B2', init: 'JV' },
  { name: 'Charles Beck', role: 'CEO, AgriGrowth Ventures', service: 'Digital Marketing', rating: 5, text: 'Our B2B customer acquisition was stagnant. Corvaflex set up a multi-channel lead gen funnel using Google Search and Meta. We went from 15 B2B leads a month to over 95 high-intent bookings. The weekly video reports are extremely transparent.', color: '#2563EB', init: 'CB' },
  { name: 'William East', role: 'Managing Partner, Elite Advisory Group', service: 'Social Media Management', rating: 5, text: 'Their executive LinkedIn content strategy is world-class. Within 60 days, our leadership team impressions grew by 350%, and we landed three enterprise-level discovery meetings directly through organic social outreach.', color: '#2563EB', init: 'WE' },
  { name: 'Thomas K.', role: 'Support Director, WearNova', service: 'AI Chatbots', rating: 5, text: 'I was skeptical about AI chatbots, but the custom customer service agent they built handles our FAQs beautifully. It resolved 45% of customer tickets instantly without human intervention. The setup paid for itself in three weeks.', color: '#D97706', init: 'TK' },
  { name: 'Laura Bennett', role: 'Director, Peak Performance Inc.', service: 'Analytics & Reporting', rating: 5, text: 'The comprehensive digital marketing audit they performed was eye-opening. They pointed out tracking errors that were misreporting our ROI by 30%. Their data-first approach saved us from making bad budget decisions.', color: '#D97706', init: 'LB' },
  { name: 'Daniel Harris', role: 'CEO, NextGen Media', service: 'Digital Marketing', rating: 5, text: 'They took over our full-funnel digital strategy. We saw a 35% reduction in customer acquisition cost and a 2.4x increase in signups in the first quarter. Truly a partnership, not just an agency.', color: '#2563EB', init: 'DH' },
  { name: 'Sophia Peterson', role: 'Brand Lead, BlueCoast Real Estate', service: 'Brand Identity & Design', rating: 5, text: 'Their brand identity redesign was flawless. They didn\'t just design a logo; they built a complete visual design system and brand manual that our team uses daily. The feedback from our clients has been phenomenal.', color: '#7C3AED', init: 'SP' }
];

const BOOKING_SERVICES = [
  'Digital Marketing', 'SEO & Content', 'Web Development', 'Mobile App Development',
  'Google Ads (PPC)', 'Facebook & Instagram Ads', 'Social Media Management', 'AI Chatbots',
  'Marketing Automation', 'Brand Identity & Design', 'Analytics & Reporting', 'E-Commerce Solutions',
];

// ─── COMPONENTS ──────────────────────────────────────────────────────────

// 3D Tilt Wrapper Component
function TiltCard({ children, className, style, onClick }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xc = rect.width / 2;
    const yc = rect.height / 2;
    const rotateX = ((yc - y) / yc) * 12; 
    const rotateY = ((x - xc) / xc) * 12; 
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px) scale3d(1.02, 1.02, 1.02)`;
    card.style.boxShadow = `0 20px 40px rgba(37, 99, 235, 0.18)`;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0) scale3d(1, 1, 1)`;
    card.style.boxShadow = ``;
  };

  return (
    <div
      ref={cardRef}
      className={className}
      style={{
        transition: 'transform 0.15s ease-out, box-shadow 0.2s ease-out',
        transformStyle: 'preserve-3d',
        ...style
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      <div style={{ transform: 'translateZ(20px)', width: '100%', height: '100%' }}>
        {children}
      </div>
    </div>
  );
}

// Responsive Navbar with sliding Hamburger Menu Drawer and clean Home Navigation
function Navbar({ onNavigateHome }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleClick = (e) => {
    setMenuOpen(false);
    if (onNavigateHome) {
      onNavigateHome();
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <div className="nav-logo" onClick={handleClick} style={{ cursor: 'pointer' }}>
          Corva<span>flex</span>
        </div>
        
        {/* Hamburger Menu Button */}
        <button className={`hamburger ${menuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Toggle menu">
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        {/* Sliding Navigation Links */}
        <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <li><a href="#services" onClick={handleClick}>Services</a></li>
          <li><a href="#why" onClick={handleClick}>Why Us</a></li>
          <li><a href="#reviews" onClick={handleClick}>Reviews</a></li>
          {/* Opens dedicated booking page in a new tab */}
          <li><a href="/?page=booking" target="_blank" onClick={() => setMenuOpen(false)}>Book a Call</a></li>
          <li><a href="#contact" onClick={handleClick}>Contact</a></li>
          <li className="mobile-cta-li">
            <a href="/?page=booking" target="_blank" className="btn-primary mobile-cta" onClick={() => setMenuOpen(false)}>Free Consultation →</a>
          </li>
        </ul>
        
        {/* Desktop Call to Action */}
        <a href="/?page=booking" target="_blank" className="btn-primary nav-desktop-cta">Free Consultation →</a>
      </div>
    </nav>
  );
}

function Hero({ onSelectService }) {
  const handleExploreServices = (e) => {
    e.preventDefault();
    const sec = document.getElementById('services');
    if (sec) {
      sec.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero">
      <div className="sphere-3d sphere-1"></div>
      <div className="sphere-3d sphere-2"></div>
      
      <div className="hero-inner">
        <div>
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            Digital Growth Agency
          </div>
          <h1>Grow Faster with <em>Data-Driven</em> Digital Marketing</h1>
          <p className="hero-desc">
            Corvaflex helps ambitious businesses scale through performance marketing, intelligent automation, and technology that converts. No guesswork — just results.
          </p>
          <div className="hero-actions">
            <a href="/?page=booking" target="_blank" className="btn-primary">Book a Free Strategy Call →</a>
            <a href="#services" onClick={handleExploreServices} className="btn-outline" style={{background:'transparent',color:'rgba(255,255,255,0.75)',borderColor:'rgba(255,255,255,0.2)'}}>Explore Services</a>
          </div>
          <div className="hero-trust">
            <div className="hero-trust-avatars">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&h=100&q=80" alt="Sarah" />
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&h=100&q=80" alt="John" />
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&h=100&q=80" alt="Emily" />
              <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&h=100&q=80" alt="David" />
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&h=100&q=80" alt="Chloe" />
            </div>
            <div className="hero-trust-text"><strong>200+ businesses</strong> growing with Corvaflex</div>
          </div>
        </div>
        <div className="hero-visual">
          {[
            { icon: '📈', title: 'Average ROI', stat: '4.8x', desc: 'across all client campaigns' },
            { icon: '🎯', title: 'Leads Generated', stat: '50K+', desc: 'for our clients this year' },
            { icon: '🌐', title: 'Sites Launched', stat: '120+', desc: 'web & mobile projects' },
            { icon: '⭐', title: 'Client Rating', stat: '4.9/5', desc: 'across all services' },
          ].map(c => (
            <TiltCard className="hero-card" key={c.title}>
              <div className="hero-card-icon">{c.icon}</div>
              <h4>{c.title}</h4>
              <p>{c.desc}</p>
              <div className="hero-card-stat">{c.stat}</div>
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClientsStrip() {
  const companies = [
    'Logix Trading', 'StyleHive', 'TechNova Solutions', 'BrightEdu Group', 
    'AutoZone USA', 'SwiftCourier', 'GreenLeaf Inc', 'Apex Logistics', 
    'FreshMart Group', 'Faruq Financial'
  ];
  const marqueeItems = [...companies, ...companies, ...companies];

  return (
    <div className="clients-strip">
      <div className="clients-strip-inner">
        <span className="clients-label">Trusted by</span>
        <div className="clients-marquee-container">
          <div className="clients-marquee-track">
            {marqueeItems.map((n, i) => (
              <span key={i} className="client-pill">{n}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Services({ onSelectService }) {
  return (
    <section className="services-section" id="services">
      <div className="container">
        <div className="services-header">
          <div className="section-eyebrow">What We Do</div>
          <h2 className="section-title">Every service you need to dominate online</h2>
          <p className="section-sub">From first click to closed deal — we cover the entire digital growth stack under one roof.</p>
        </div>
        <div className="services-grid">
          {SERVICES.map(s => (
            <TiltCard 
              className="service-card clickable-card" 
              key={s.title}
              onClick={() => onSelectService(s)}
            >
              <div className="svc-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <span className="service-tag">Learn Details →</span>
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBand() {
  const stats = [
    { n: '200', s: '+', d: 'Happy Clients' },
    { n: '50K', s: '+', d: 'Leads Generated' },
    { n: '4.8', s: 'x', d: 'Average ROI' },
    { n: '99', s: '%', d: 'Client Retention' },
  ];
  const marqueeStats = [...stats, ...stats, ...stats, ...stats];

  return (
    <div className="stats-band">
      <div className="stats-marquee-container">
        <div className="stats-marquee-track">
          {marqueeStats.map((s, i) => (
            <div className="stat-item-marquee" key={i}>
              <div className="stat-num">{s.n}<span>{s.s}</span></div>
              <div className="stat-desc">{s.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WhyUs() {
  const [animate, setAnimate] = useState(false);
  const dashboardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setAnimate(true);
        observer.disconnect();
      }
    }, { threshold: 0.15 });

    if (dashboardRef.current) {
      observer.observe(dashboardRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const metrics = [
    { label: 'Lead Quality Score', val: '94%', width: '94%' },
    { label: 'Campaign ROI', val: '480%', width: '88%' },
    { label: 'Client Retention', val: '99%', width: '99%' },
    { label: 'On-time Delivery', val: '97%', width: '97%' },
  ];

  return (
    <section className="why-section" id="why">
      <div className="why-inner container" style={{maxWidth:'1200px',margin:'0 auto'}}>
        <div>
          <div className="section-eyebrow">Why Corvaflex</div>
          <h2 className="section-title">We treat your budget like our own</h2>
          <p className="section-sub">Most agencies chase vanity metrics. We build systems that generate real revenue — and show you exactly how.</p>
          <div className="why-features">
            {[
              { icon: '🔬', title: 'Data-first decisions', desc: 'Every campaign decision is backed by real data — no gut feelings, no wasted spend.' },
              { icon: '🤝', title: 'Dedicated account team', desc: 'One point of contact who knows your business and actually picks up the phone.' },
              { icon: '📅', title: 'Weekly transparent reporting', desc: 'You see exactly what is running, what is working, and where every rupee went.' },
              { icon: '⚡', title: 'Agile execution', desc: 'Fast turnarounds, quick pivots, and no bureaucracy slowing down your growth.' },
            ].map(f => (
              <div className="why-feature" key={f.title}>
                <div className="why-icon">{f.icon}</div>
                <div>
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div ref={dashboardRef} className="why-visual">
          <div className="why-visual-title">Performance Dashboard</div>
          {metrics.map(m => (
            <div className="why-metric" key={m.label}>
              <span className="why-metric-label">{m.label}</span>
              <div className="why-metric-bar-wrap">
                <div 
                  className="why-metric-bar" 
                  style={{
                    width: animate ? m.width : '0%',
                    transition: 'width 1.8s cubic-bezier(0.25, 1, 0.5, 1) 0.1s'
                  }}
                ></div>
              </div>
              <span className="why-metric-val">{m.val}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Extended 6-step Process Carousel Slider (Marketing & AI Automation Focus)
function Process() {
  const steps = [
    { n: '1', title: 'Audit & Discovery', desc: 'We deep-dive into your existing marketing channels, analytics logs, and software systems to identify bottleneck leaks.' },
    { n: '2', title: 'AI & Automation Mapping', desc: 'We map out custom CRM schemas, trigger-based communication loops, and train mockups for support/sales chatbots.' },
    { n: '3', title: 'Funnel & Strategy Setup', desc: 'We structure acquisition campaigns, design high-converting visual assets, and map custom client React portals.' },
    { n: '4', title: 'Integration & Coding', desc: 'Our technical team codes responsive web modules, deploys backend database scripts, and syncs API webhooks.' },
    { n: '5', title: 'Campaign Launch', desc: 'We launch paid search, shopping, and retargeting ads, turning on synchronized email/SMS sequences and GA4 analytics.' },
    { n: '6', title: 'Optimize & Scale', desc: 'We run weekly creative tests, negative keyword audits, and expand marketing budgets on high-ROI ad sets to drive scaling.' },
  ];

  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [steps.length]);

  const handleDotClick = (index) => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile && index > steps.length - 3) {
      setActiveStep(steps.length - 3);
    } else {
      setActiveStep(index);
    }
  };

  return (
    <section className="process-section">
      <div className="container">
        <div className="section-eyebrow">Our Process</div>
        <h2 className="section-title">From brief to results in days, not months</h2>
        <p className="section-sub">A premium, six-step engagement model designed for maximum marketing performance and digital automation setup.</p>
        
        <div className="process-slider-container">
          <div className="process-slider-track" style={{ '--active-step': activeStep }}>
            {steps.map((s, i) => (
              <div className={`process-slide-card ${activeStep === i ? 'active' : ''}`} key={s.n} onClick={() => handleDotClick(i)}>
                <div className="step-circle">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="process-dots">
          {steps.map((_, i) => (
            <button 
              key={i} 
              className={`process-dot ${activeStep === i ? 'active' : ''}`} 
              onClick={() => handleDotClick(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function StarRating({ rating }) {
  return (
    <div className="review-stars">
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </div>
  );
}

// Parametrized Reviews Component
function Reviews({ limit, showAddForm = true }) {
  const [reviews, setReviews] = useState(REVIEWS);
  const [form, setForm] = useState({ name: '', role: '', service: '', rating: 5, text: '' });
  const [submitted, setSubmitted] = useState(false);
  const [starHover, setStarHover] = useState(0);

  const COLORS = ['#2563EB','#7C3AED','#0891B2','#059669','#DC2626','#D97706'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.text || !form.service) return;
    const init = form.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
    const color = COLORS[reviews.length % COLORS.length];
    setReviews(prev => [{ ...form, init, color }, ...prev]);
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm({ name:'', role:'', service:'', rating:5, text:'' }); }, 3000);
  };

  const displayedReviews = limit ? reviews.slice(0, limit) : reviews;

  return (
    <section className="reviews-section" id="reviews">
      <div className="container">
        <div className="section-eyebrow">Client Reviews</div>
        <h2 className="section-title">Real results, real people</h2>
        <p className="section-sub">Over 200 businesses have grown with Corvaflex. Here is what some of them have to say.</p>

        <div className="reviews-grid">
          {displayedReviews.map((r, i) => (
            <TiltCard className="review-card" key={i}>
              <div className="review-service-tag">{r.service}</div>
              <StarRating rating={r.rating} />
              <p className="review-text">"{r.text}"</p>
              <div className="review-author">
                <div className="review-avatar" style={{background: r.color}}>{r.init}</div>
                <div>
                  <div className="review-name">{r.name}</div>
                  <div className="review-role">{r.role}</div>
                </div>
              </div>
            </TiltCard>
          ))}
        </div>

        {/* View All Reviews Button (homepage only) */}
        {limit && (
          <div style={{ textAlign: 'center', marginTop: '3.5rem' }}>
            <a href="/?page=reviews" target="_blank" className="btn-primary">
              View All Reviews ({reviews.length}) →
            </a>
          </div>
        )}

        {/* Add Review Form (dedicated page only) */}
        {showAddForm && (
          <div className="add-review-wrap">
            <h3 className="add-review-title">Share Your Experience</h3>
            <p className="add-review-sub">Worked with Corvaflex? We would love to hear from you.</p>
            {submitted ? (
              <div className="form-success">✅ Thank you! Your review has been posted.</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name *</label>
                    <input placeholder="Enter your name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required />
                  </div>
                  <div className="form-group">
                    <label>Role / Company</label>
                    <input placeholder="Enter your role / company" value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Service Used *</label>
                  <select value={form.service} onChange={e => setForm(p => ({...p, service: e.target.value}))} required>
                    <option value="">Select a service…</option>
                    {BOOKING_SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Your Rating</label>
                  <div className="star-picker">
                    {[1,2,3,4,5].map(n => (
                      <button type="button" key={n} className={(starHover || form.rating) >= n ? 'active' : ''}
                        onMouseEnter={() => setStarHover(n)} onMouseLeave={() => setStarHover(0)}
                        onClick={() => setForm(p => ({...p, rating: n}))}>★</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Your Review *</label>
                  <textarea placeholder="Enter your review" value={form.text} onChange={e => setForm(p => ({...p, text: e.target.value}))} required />
                </div>
                <button type="submit" className="btn-primary" style={{width:'100%', justifyContent:'center'}}>Post Review →</button>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// Booking Form Component with clean custom placeholders
function Booking() {
  const [form, setForm] = useState({ name:'', email:'', phone:'', services:[], message:'' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const toggleService = (s) => {
    setForm(p => ({
      ...p,
      services: p.services.includes(s) ? p.services.filter(x => x !== s) : [...p.services, s]
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.match(/^[^@]+@[^@]+\.[^@]+$/)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    // Google Sheets Integration POST Request
    if (GOOGLE_SHEETS_WEBAPP_URL && GOOGLE_SHEETS_WEBAPP_URL !== '') {
      fetch(GOOGLE_SHEETS_WEBAPP_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          services: form.services.join(', '),
          message: form.message,
          timestamp: new Date().toISOString()
        })
      })
      .then(() => console.log('Booking lead logged to Google Sheets successfully.'))
      .catch(err => console.error('Failed to log booking lead to Google Sheets:', err));
    }

    setSubmitted(true);
  };

  return (
    <div className="booking-form-wrap">
      <h3>Book Your Free Strategy Call</h3>
      <p className="sub">Takes 2 minutes. Our team responds within 2 hours.</p>
      {submitted ? (
        <div className="booking-success">
          <div className="booking-success-icon">🎉</div>
          <h4>You are booked in!</h4>
          <p>We will reach out to <strong>{form.email}</strong> within a few hours to confirm your call time.</p>
          <br />
          <p style={{color:'#64748B', fontSize:'0.85rem'}}>Can not wait? Call us at <strong>+1(845) 243-3484</strong> or WhatsApp us at <strong>+92 319 6552092</strong></p>
        </div>
      ) : (
        <form className="booking-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label>Full Name *</label>
            <input placeholder="Enter your name" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} style={errors.name ? {borderColor:'var(--error)'} : {}} />
            {errors.name && <span style={{color:'var(--error)',fontSize:'0.78rem'}}>{errors.name}</span>}
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input type="email" placeholder="Enter your email" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} style={errors.email ? {borderColor:'var(--error)'} : {}} />
            {errors.email && <span style={{color:'var(--error)',fontSize:'0.78rem'}}>{errors.email}</span>}
          </div>
          <div className="form-group">
            <label>Phone / WhatsApp *</label>
            <input type="tel" placeholder="Enter your phone number" value={form.phone} onChange={e => setForm(p=>({...p,phone:e.target.value}))} style={errors.phone ? {borderColor:'var(--error)'} : {}} />
            {errors.phone && <span style={{color:'var(--error)',fontSize:'0.78rem'}}>{errors.phone}</span>}
          </div>
          <div className="form-group">
            <label>Services You Are Interested In</label>
            <div className="service-select-grid">
              {BOOKING_SERVICES.map(s => (
                <button type="button" key={s} className={`service-opt${form.services.includes(s) ? ' selected' : ''}`} onClick={() => toggleService(s)}>{s}</button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Anything else we should know?</label>
            <textarea placeholder="Enter your message" value={form.message} onChange={e => setForm(p=>({...p,message:e.target.value}))} style={{minHeight:'80px'}} />
          </div>
          <button type="submit" className="btn-primary booking-submit">Book My Free Call →</button>
        </form>
      )}
    </div>
  );
}

function ContactStrip() {
  return (
    <div className="contact-strip" id="contact">
      <div className="contact-strip-inner">
        <div>
          <h3>Ready to grow? Let us talk.</h3>
          <p>Reach us directly — we respond fast.</p>
        </div>
        <div className="contact-strip-actions">
          <div className="contact-detail">
            📧 <a href="mailto:corvaflex@gmail.com">corvaflex@gmail.com</a>
          </div>
          <div className="contact-detail">
            📞 <a href="tel:+18452433484">+1(845) 243-3484</a>
          </div>
          <a href="https://wa.me/923196552092" className="btn-white" target="_blank" rel="noreferrer">WhatsApp Us →</a>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="nav-logo" style={{fontSize:'1.55rem'}}>Corva<span>flex</span></div>
            <p>A leading digital marketing and automation agency. We help businesses grow through data-driven strategy and intelligent technology.</p>
            
            <div className="footer-socials">
              <a href="https://www.linkedin.com/in/corva-flex" target="_blank" rel="noreferrer" className="social-btn" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61592165703830" target="_blank" rel="noreferrer" className="social-btn" aria-label="Facebook">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/corva_flex?igsh=NWZmY3RleDJudTJx" target="_blank" rel="noreferrer" className="social-btn" aria-label="Instagram">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
            </div>
          </div>
          <div className="footer-col">
            <h4>Services</h4>
            <ul>
              {['Digital Marketing','SEO & Content','Web Development','Mobile App Development','Google Ads (PPC)','Facebook & Instagram Ads'].map(s => <li key={s}><a href="#services">{s}</a></li>)}
            </ul>
          </div>
          <div className="footer-col">
            <h4>More Services</h4>
            <ul>
              {['Social Media Management','AI Chatbots','Marketing Automation','Brand Identity & Design','Analytics & Reporting','E-Commerce Solutions'].map(s => <li key={s}><a href="#services">{s}</a></li>)}
            </ul>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <ul>
              <li><a href="mailto:corvaflex@gmail.com">corvaflex@gmail.com</a></li>
              <li><a href="tel:+18452433484">+1(845) 243-3484</a></li>
              <li><a href="https://wa.me/923196552092" target="_blank" rel="noreferrer">WhatsApp</a></li>
              <li><a href="#booking">Book a Call</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Corvaflex. All rights reserved.</p>
          <p>Global Digital Marketing & Automation Agency</p>
        </div>
      </div>
    </footer>
  );
}

// Service Detail Page Component
function ServiceDetail({ service, onBack }) {
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [service]);

  const detail = SERVICE_DETAILS[service.title] || {
    tagline: 'Scale operations and increase acquisition.',
    overview: service.desc,
    features: ['Custom roadmap & timeline setup', 'Dedicated growth specialist support', 'Real-time performance checks', 'Multi-channel attribution analytics'],
    faqs: [
      { q: 'How do we begin?', a: 'Book a free strategy audit call below. We will analyze your baseline operations and return a customized execution blueprint.' },
      { q: 'What is the standard contract period?', a: 'We work on a flexible month-to-month basis, prioritizing long-term growth and partnerships over restrictive lock-in agreements.' }
    ]
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <section className="service-detail-section">
      <div className="container">
        <button onClick={onBack} className="btn-back">
          <span>←</span> Back to Services
        </button>
        
        <div className="service-detail-hero">
          <div className="svc-icon-large">{service.icon}</div>
          <div className="service-detail-badge">{service.tag}</div>
          <h1 className="service-detail-title">{service.title}</h1>
          <p className="service-detail-tagline">{detail.tagline}</p>
          <p className="service-detail-overview">{detail.overview}</p>
        </div>
        
        <div className="service-detail-grid">
          <div className="service-detail-features">
            <h2>Key Capabilities & Deliverables</h2>
            <div className="features-list">
              {detail.features.map((feat, i) => (
                <div className="feature-item-detail" key={i}>
                  <div className="feature-dot">✓</div>
                  <h4>{feat}</h4>
                </div>
              ))}
            </div>
          </div>
          
          <div className="service-detail-timeline">
            <h2>Our Operational Blueprint</h2>
            <div className="timeline-steps-detail">
              {[
                { step: '01', title: 'Audit & Align', desc: 'We inspect your current traffic patterns, competitor placements, and ad history to spot leaks.' },
                { step: '02', title: 'Execute & Launch', desc: 'Our team designs visual creative assets, codes templates, and deploys structures in days.' },
                { step: '03', title: 'Optimize & Scale', desc: 'We review tracking weekly, scale budgets on winning campaigns, and continuously refine.' }
              ].map((item, i) => (
                <div className="timeline-item-detail" key={i}>
                  <div className="timeline-num-detail">{item.step}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="service-detail-faqs">
          <h2>Frequently Asked Questions</h2>
          <div className="faqs-accordion">
            {detail.faqs.map((faq, i) => (
              <div className={`faq-card-detail ${activeFaq === i ? 'active' : ''}`} key={i}>
                <button className="faq-question-btn" onClick={() => toggleFaq(i)}>
                  <span>{faq.q}</span>
                  <span className="faq-icon-arrow">{activeFaq === i ? '−' : '+'}</span>
                </button>
                <div className="faq-answer-content" style={{ maxHeight: activeFaq === i ? '200px' : '0' }}>
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="service-detail-cta">
          <h3>Ready to scale your business with {service.title}?</h3>
          <p>Get a custom strategy blueprint built specifically for your goals. No sales pitch, no commitment.</p>
          <a href="/?page=booking" target="_blank" className="btn-white">Book Your Free Call Now →</a>
        </div>
      </div>
    </section>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────

export default function App() {
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'service-detail', 'booking-page', 'reviews-page'
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') === 'booking') {
      setCurrentPage('booking-page');
    } else if (params.get('page') === 'reviews') {
      setCurrentPage('reviews-page');
    }
  }, []);

  const handleSelectService = (svc) => {
    setActiveService(svc);
    setCurrentPage('service-detail');
  };

  const handleNavigateHome = () => {
    setActiveService(null);
    setCurrentPage('home');
  };

  // Dedicated full-page Booking template (opens in a new tab/link)
  if (currentPage === 'booking-page') {
    return (
      <>
        <Navbar onNavigateHome={() => window.location.href = '/'} />
        <div style={{ paddingTop: '130px', background: 'var(--navy)', minHeight: '90vh', paddingBottom: '5rem' }}>
          <div className="container" style={{ maxWidth: '650px' }}>
            <TiltCard className="booking-form-card" style={{ background: 'var(--white)' }}>
              <Booking />
            </TiltCard>
          </div>
        </div>
        <ContactStrip />
        <Footer />
      </>
    );
  }

  // Dedicated full-page Reviews page showing all 20 reviews and the "Add Review" form
  if (currentPage === 'reviews-page') {
    return (
      <>
        <Navbar onNavigateHome={() => window.location.href = '/'} />
        <div style={{ paddingTop: '100px', background: 'var(--gray-bg)', minHeight: '90vh', paddingBottom: '5rem' }}>
          <Reviews showAddForm={true} />
        </div>
        <ContactStrip />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar onNavigateHome={handleNavigateHome} />
      {currentPage === 'service-detail' && activeService ? (
        <ServiceDetail service={activeService} onBack={handleNavigateHome} />
      ) : (
        <>
          <Hero onSelectService={handleSelectService} />
          <ClientsStrip />
          <Services onSelectService={handleSelectService} />
          <StatsBand />
          <WhyUs />
          <Process />
          {/* cap the reviews display on the homepage to 3, and do not show the review submission form */}
          <Reviews limit={3} showAddForm={false} />
          <section className="booking-section" id="booking">
            <div className="booking-inner">
              <div className="booking-info">
                <div className="section-eyebrow" style={{color:'#93c5fd'}}>
                  <span style={{background:'#93c5fd'}}></span>Book a Free Call
                </div>
                <h2>Let us build your growth plan — for free</h2>
                <p>In 30 minutes, we will audit your current digital presence, identify your biggest opportunities, and outline exactly how Corvaflex can help you scale.</p>
                <div className="booking-perks">
                  {[
                    'No obligation, no sales pressure',
                    'Personalized audit of your digital channels',
                    'Clear roadmap with estimated outcomes',
                    'Dedicated strategist, not a junior intern',
                    'Follow-up proposal within 24 hours',
                  ].map(p => (
                    <div className="booking-perk" key={p}>
                      <div className="perk-check">✓</div>
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="booking-form-card">
                <Booking />
              </div>
            </div>
          </section>
        </>
      )}
      <ContactStrip />
      <Footer />
    </>
  );
}
